/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Minutes, Program, Time, TimeUtils, Seconds } from '@wit/vtv-x-foundation-lib';
import { getProgramCurrentProgress } from 'services/utils/ProgramUtils';
import { ProgressBar } from 'ui/components';
import { PROGRAM_MARGIN } from './TVGuideLine';
import { TimeLimits } from './TVGuideGrid';
import useIsAdultContent from '../../../../hooks/useIsAdultContent';
import useAssetTitle from '../../../../hooks/useAssetTitle';
import Badge from '../../Badge';

interface Props {
  program: Program;
  unitSize: number;
  unitDuration: Minutes;
  timeLimits: TimeLimits;
  onClick: (program: Program) => void;
  now: Time;
  ctxId: string | number;
}

const SIX_MINUTES_VALUE = 35;
const FIFTEEN_MINUTES_VALUE = 100;

function TvGuideCard({ program, onClick, now, unitSize, unitDuration, timeLimits, ctxId }: Props): JSX.Element {
  const bemModifier = `${ctxId}-${program.id > 0 ? program.id : TimeUtils.toUnixEpoch(program.startDate)}`;
  const [progress, setProgress] = useState(0);
  const [isLive, setIsLive] = useState(false);
  const [minProgressWidth, setMinProgressWidth] = useState(0);
  const isAdultContent = useIsAdultContent(program);

  const parentWidth = useMemo(() => {
    const start: Time = TimeUtils.isAfter(program.startDate, timeLimits.start) ? program.startDate : timeLimits.start;
    const end: Time = TimeUtils.isBefore(program.endDate, timeLimits.end) ? program.endDate : timeLimits.end;
    const duration: Seconds = TimeUtils.toUnixEpoch(end) - TimeUtils.toUnixEpoch(start);
    const size =
      (duration * unitSize) / TimeUtils.minutesToSeconds(unitDuration) - (end !== timeLimits.end ? PROGRAM_MARGIN : 0);
    return size > 0 ? size : 1;
  }, [program, timeLimits, unitDuration, unitSize]);

  useEffect(() => {
    const newProgress = getProgramCurrentProgress(program, now);
    setProgress(newProgress);
    setMinProgressWidth(((parentWidth + PROGRAM_MARGIN) * newProgress) / 100);
    setIsLive(newProgress > 0 && newProgress < 100);
  }, [program, now, parentWidth]);

  const [isMouseOver, setIsMouseOver] = useState(false);

  const click = useCallback(() => {
    if (!isAdultContent) {
      onClick(program);
    }
  }, [isAdultContent, onClick, program]);

  const programTitle = useAssetTitle(program);

  return (
    <li style={{ width: parentWidth }}>
      <div
        className={`epg-card ${isAdultContent ? 'adult' : ''} ${
          parentWidth < FIFTEEN_MINUTES_VALUE ? 'hide-content' : ''
        }`}
        role="button"
        tabIndex={0}
        onMouseOver={() => setIsMouseOver(true)}
        onMouseLeave={() => setIsMouseOver(false)}
        onClick={click}
        id={`TVGuideCard___${bemModifier}`}
      >
        <div style={{ minWidth: parentWidth }}>
          <span className="epg-card-title" id={`TVGuideCard_title___${bemModifier}`}>
            {programTitle}
          </span>
          <div className="epg-card-footer">
            {!isAdultContent ? (
              <div className="badges-holder" id={`TVGuideCard_badges___${bemModifier}`}>
                {program.badges?.map((badge) => (
                  <Badge key={badge.icon} badge={badge} bemBlock="Card" bemModifierPrefix={bemModifier} />
                ))}
              </div>
            ) : null}
            <span className="epg-card-time" id={`TVGuideCard_time___${bemModifier}`}>
              {program.startTime} - {program.endTime}
            </span>
          </div>
          {parentWidth >= SIX_MINUTES_VALUE && parentWidth <= FIFTEEN_MINUTES_VALUE && (
            <div className="epg-card-filler" id={`TVGuideCard_filler___${bemModifier}`}>
              <span>...</span>
            </div>
          )}
          {program && TimeUtils.isBetweenInclusive(program.startDate, program.endDate, now) ? (
            <ProgressBar
              percentage={progress}
              isActive={isMouseOver}
              minWidth={minProgressWidth}
              bemModifier={bemModifier}
            />
          ) : null}
          <div
            className={`epg-card-background ${isLive ? 'epg-card-background-live' : ''}`}
            style={{ width: `${progress}%`, minWidth: `${minProgressWidth}px` }}
          />
        </div>
      </div>
    </li>
  );
}

export default React.memo(TvGuideCard);
